import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ClientNavbar } from '../components/ClientNavbar';
import { CompleteProfileModal } from '../components/CompleteProfileModal';
import { Skeleton } from '../components/ui/skeleton';
import { CheckCircle2, Zap } from 'lucide-react';
import { API, BACKEND_URL } from '../utils/api';

// Resolve a possibly-relative image URL to an absolute one
function resolveImageUrl(url) {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${BACKEND_URL}${url}`;
}

// Gradient backgrounds for cards (replaces real sport images)
const gradients = [
    'from-blue-700 via-blue-900 to-indigo-950',
    'from-indigo-800 via-purple-900 to-slate-950',
    'from-violet-700 via-purple-900 to-pink-950',
    'from-emerald-700 via-teal-900 to-cyan-950',
    'from-orange-700 via-red-800 to-rose-950',
];


export default function Dashboard() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [userPredictions, setUserPredictions] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const eventsRes = await axios.get(`${API}/events`);
            setEvents(eventsRes.data);

            const predictionsMap = {};
            for (const event of eventsRes.data) {
                try {
                    const predRes = await axios.get(
                        `${API}/events/${event.event_id}/user-predictions`,
                        { withCredentials: true }
                    );
                    if (predRes.data.length > 0) {
                        predictionsMap[event.event_id] = predRes.data;
                    }
                } catch (e) {
                    // User not logged in or no predictions
                }
            }
            setUserPredictions(predictionsMap);
            refreshUser();
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEventClick = (event) => {
        if (event.status === 'completed') return;
        navigate(`/event/${event.event_id}`);
    };

    return (
        <div className="min-h-screen bg-background md:ml-64">
            <CompleteProfileModal />
            <ClientNavbar />

            <main className="max-w-[1200px] mx-auto py-10 px-6 sm:px-8">

                {/* ── Page Header ── */}
                <header className="mb-10">
                    <h1 className="text-2xl font-black text-[#001944] dark:text-white tracking-tight uppercase mb-2">
                        Eventos Disponibles
                    </h1>
                    <div className="h-1 w-12 bg-[#4c56af] rounded-full" />
                </header>

                {/* ── Events Grid ── */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-80 rounded-xl" />
                        ))}
                    </div>
                ) : events.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {events.filter(event => event.status !== 'completed').map((event, index) => {
                            const gradient = gradients[index % gradients.length];
                            const hasPredictions = userPredictions[event.event_id];
                            const isCompleted = event.status === 'completed';

                            return (
                                <div
                                    key={event.event_id}
                                    className={`group relative overflow-hidden rounded-xl
                                        bg-white dark:bg-slate-900
                                        shadow-sm hover:shadow-md
                                        transition-all duration-300
                                        ${!isCompleted ? 'cursor-pointer' : 'cursor-default opacity-75'}
                                    `}
                                    onClick={() => handleEventClick(event)}
                                    data-testid={`event-card-${event.event_id}`}
                                >
                                    {/* ── Image / Gradient Area ── */}
                                    <div className="relative h-48 overflow-hidden">
                                        {/* Real image if available, gradient fallback */}
                                        {event.image_url ? (
                                            <img
                                                src={resolveImageUrl(event.image_url)}
                                                alt={event.name}
                                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div
                                                className={`absolute inset-0 bg-gradient-to-br ${gradient}
                                                    group-hover:scale-105 transition-transform duration-500`}
                                            />
                                        )}

                                        {/* Dark gradient overlay for text readability */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#001944]/90 via-[#001944]/20 to-transparent" />

                                        {/* ── Status & Category Badges ── */}
                                        <div className="absolute top-4 left-4 flex gap-2">
                                            {/* Status badge */}
                                            {event.status === 'active' && (
                                                <span className="px-2 py-1 bg-red-600 text-white text-[10px] font-bold rounded uppercase tracking-wider flex items-center gap-1">
                                                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                                    LIVE
                                                </span>
                                            )}
                                            {event.status === 'closed' && (
                                                <span className="px-2 py-1 bg-amber-500 text-white text-[10px] font-bold rounded uppercase tracking-wider">
                                                    CERRADO
                                                </span>
                                            )}
                                            {event.status === 'completed' && (
                                                <span className="px-2 py-1 bg-slate-600 text-white text-[10px] font-bold rounded uppercase tracking-wider">
                                                    FINALIZADO
                                                </span>
                                            )}

                                            {/* Sport type badge */}
                                            {event.sport_type && (
                                                <span className="px-2 py-1 bg-white/20 backdrop-blur-md text-white text-[10px] font-bold rounded uppercase tracking-wider">
                                                    {event.sport_type}
                                                </span>
                                            )}
                                        </div>

                                        {/* Already participated indicator */}
                                        {hasPredictions && (
                                            <div className="absolute top-4 right-4 bg-emerald-500 text-white p-1.5 rounded-full shadow-lg animate-pulse">
                                                <CheckCircle2 size={14} />
                                            </div>
                                        )}

                                        {/* ── Event Title Overlay ── */}
                                        <div className="absolute bottom-4 left-4 right-4">
                                            <h3 className="text-white font-bold text-lg leading-tight line-clamp-2">
                                                {event.name}
                                            </h3>
                                            <p className="text-white/70 text-xs font-medium uppercase tracking-widest mt-1 truncate">
                                                {event.description || event.sport_type}
                                            </p>
                                        </div>
                                    </div>

                                    {/* ── Card Body ── */}
                                    <div className="p-5 flex flex-col gap-4">


                                        {/* PARTICIPAR button */}
                                        {(() => {
                                            const isClosed = event.status === 'closed';
                                            return (
                                                <button
                                                    className={`w-full py-3 text-xs font-black tracking-widest rounded-lg transition-colors uppercase
                                                        ${isCompleted
                                                            ? 'bg-slate-200 dark:bg-white/10 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                                                            : isClosed
                                                                ? 'bg-[#4c56af] text-white hover:bg-[#3a4498]'
                                                                : 'bg-[#001944] dark:bg-[#4c56af] text-white hover:bg-[#002c6d] dark:hover:bg-[#3a4498]'
                                                        }
                                                    `}
                                                    disabled={isCompleted}
                                                    onClick={(e) => { e.stopPropagation(); isClosed ? navigate('/ranking') : handleEventClick(event); }}
                                                >
                                                    {isCompleted ? 'FINALIZADO' : isClosed ? 'VER RANKING' : 'JUGAR POLLA'}
                                                </button>
                                            );
                                        })()}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-20 bg-white dark:bg-slate-900 border border-dashed border-slate-200 dark:border-white/10 rounded-xl">
                        <Zap size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                        <h3 className="text-lg font-bold text-slate-500 dark:text-slate-400">
                            No hay eventos disponibles
                        </h3>
                        <p className="text-slate-400 dark:text-slate-500 mt-2 text-sm">
                            Los nuevos eventos aparecerán aquí pronto.
                        </p>
                    </div>
                )}
            </main>
        </div>
    );
}
